import Link from "next/link";
import { MenuIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import Image from "next/image";
import { Sheet, SheetTrigger, SheetContent, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { NavItemStoryblok, SiteConfigStoryblok } from "@/types/sb-component-types";
import { getStoryblokLinkProps } from "@/lib/storyblok/get-storyblok-link-props";
import { LocaleSwitch } from "@/components/LocaleSwitch";
interface IMobileMenuProps {
  blok: SiteConfigStoryblok;
  isTransparent?: boolean;
}
export const MobileMenu = ({
  blok,
  isTransparent
}: IMobileMenuProps) => {
  const [open, setOpen] = useState(false);
  return <Sheet open={open} onOpenChange={setOpen} data-sentry-element="Sheet" data-sentry-component="MobileMenu" data-sentry-source-file="MobileMenu.tsx">
      <SheetTrigger asChild data-sentry-element="SheetTrigger" data-sentry-source-file="MobileMenu.tsx">
        <Button className={`border-none bg-transparent ${isTransparent ? "text-white hover:bg-white/80" : ""}`} size="icon" variant={isTransparent ? "ghost" : "outline"} data-sentry-element="Button" data-sentry-source-file="MobileMenu.tsx">
          <MenuIcon className="h-6 w-6" data-sentry-element="MenuIcon" data-sentry-source-file="MobileMenu.tsx" />
          <span className="sr-only">Toggle navigation menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col bg-background p-4 sm:p-6" side="right" data-sentry-element="SheetContent" data-sentry-source-file="MobileMenu.tsx">
        <div className="mb-6">
          <Link href="/" onClick={() => setOpen(false)} data-sentry-element="Link" data-sentry-source-file="MobileMenu.tsx">
            <Image alt={blok.header_logo.alt || "Logo"} className="h-10 w-auto" height={40} src={blok.header_logo.filename} width={150} data-sentry-element="Image" data-sentry-source-file="MobileMenu.tsx" />
          </Link>
        </div>
        <nav className="flex-grow">
          <MobileMenuItem href="/" title="Home" data-sentry-element="MobileMenuItem" data-sentry-source-file="MobileMenu.tsx" />
          {blok.header_nav?.map((navItem, index) => <MobileNavItem key={index} navItem={navItem} />)}
        </nav>
        <div className="mt-auto pt-4">
          <LocaleSwitch data-sentry-element="LocaleSwitch" data-sentry-source-file="MobileMenu.tsx" />
        </div>
      </SheetContent>
    </Sheet>;
};
const MobileMenuItem = ({
  href,
  title,
  onClick
}: {
  href: string;
  title: string;
  onClick?: () => void;
}) => <SheetClose asChild data-sentry-element="SheetClose" data-sentry-component="MobileMenuItem" data-sentry-source-file="MobileMenu.tsx">
    <Link className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-base font-medium transition-colors hover:bg-muted focus:outline-none focus-visible:bg-muted" href={href} prefetch={false} onClick={onClick} data-sentry-element="Link" data-sentry-source-file="MobileMenu.tsx">
      {title}
    </Link>
  </SheetClose>;
const MobileNavItem = ({
  navItem
}: {
  navItem: NavItemStoryblok;
}) => {
  if (navItem.sub_nav_items && navItem.sub_nav_items.length > 0) {
    return <Collapsible>
        <CollapsibleTrigger className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-base font-medium transition-colors hover:bg-muted focus:outline-none focus-visible:bg-muted [&[data-state=open]>svg]:rotate-90">
          <span>{navItem.title}</span>
          <ChevronRightIcon className="h-5 w-5 transition-all" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1 pl-6">
          {navItem.sub_nav_items.map((subItem, subIndex) => <MobileMenuItem key={subIndex} {...getStoryblokLinkProps(subItem.link)} title={subItem.title} />)}
        </CollapsibleContent>
      </Collapsible>;
  }
  return <MobileMenuItem href={getStoryblokLinkProps(navItem.link).href} title={navItem.title} data-sentry-element="MobileMenuItem" data-sentry-component="MobileNavItem" data-sentry-source-file="MobileMenu.tsx" />;
};